import { motion } from 'framer-motion';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import styled from 'styled-components';
import ImageMaterial from '@/assets/images/image-btcfi-material.png';
import DepositBifiPanel from '@/components/Earn/DepositBifiPanel';
import DepositEverdexPanel from '@/components/Earn/DepositEverdexPanel';
import FAQPanel from '@/components/Earn/FAQPanel';
import { FlexBox } from '@/components/atom/FlexBox';
import { StyledText } from '@/components/atom/Text';
import { MEDIA_QUERY_BREAKPOINT } from '@/configs/mediaQuery';
import {
  dashboardCardVariant,
  dashboardContainerVariant,
} from '@/styles/motion';

const DialogAction = dynamic(() => import('@/components/DialogAction'), {
  ssr: false,
});

const Container = styled(FlexBox)`
  margin-top: 64px;
`;

const DepositContainer = styled.div`
  display: grid;
  grid-template: 'a b';
  grid-template-columns: 1fr 1fr;
  gap: 40px;

  & > *:nth-child(1) {
    grid-area: a;
  }
  & > *:nth-child(2) {
    grid-area: b;
  }

  @media (${MEDIA_QUERY_BREAKPOINT}) {
    grid-template:
      'a'
      'b';
  }
`;

const PromotePanel = styled(FlexBox)`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 56px 64px;
  width: 100%;
  height: 260px;
  border-radius: 36px;
  background: radial-gradient(
    190.44% 186.74% at 100% -48.21%,
    #1a65f7 0%,
    #153576 51.5%,
    #0e204a 100%
  );
  overflow: hidden;

  img {
    position: absolute;
    top: 0;
    right: -70px;
    width: 572px;
  }

  p {
    z-index: 1;
  }

  @media (max-width: 1700px) {
    p {
      white-space: pre-line;
    }

    img {
      right: 0;
    }
  }

  @media (${MEDIA_QUERY_BREAKPOINT}) {
    padding: 46px 58px;

    img {
      right: -108px;
      width: 570px;
      z-index: 0;
    }
  }
`;

const DepositBox = styled(FlexBox)`
  @media (${MEDIA_QUERY_BREAKPOINT}) {
    flex-direction: column;
  }
`;

export default function Earn() {
  const { t } = useTranslation('earn');

  return (
    <>
      <Container
        as={motion.div}
        variants={dashboardContainerVariant}
        initial="hidden"
        animate="show"
        exit="exit"
        $flex
        $direction="column"
        $gap={64}
      >
        <PromotePanel as={motion.div} variants={dashboardCardVariant}>
          <StyledText as="p" $fontSize={40} $fontWeight="bold">
            {t('earn:bannerTitle')}
          </StyledText>
          <StyledText as="p" $fontSize="xl">
            {t('earn:bannerContent')}
          </StyledText>
          <Image src={ImageMaterial} width={430} height={442} alt="" />
        </PromotePanel>
        <DepositContainer>
          <DepositBox $flex $direction="column" $gap={40}>
            <DepositBifiPanel />
            <DepositEverdexPanel />
          </DepositBox>
          <FAQPanel />
        </DepositContainer>
      </Container>
      <DialogAction />
    </>
  );
}

export const getStaticProps = async ({ locale }: { locale: string }) => ({
  props: {
    ...(await serverSideTranslations(locale, [
      'common',
      'dialog',
      'tooltip',
      'earn',
      'dashboard',
    ])),
  },
});
